import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Award, Heart, Download, ArrowLeft } from "lucide-react";

export default function Certificate() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [cert, setCert] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!user) return;
    api.get(`/me/certificates/${courseId}`)
      .then((r) => setCert(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Certificate not earned yet."));
  }, [courseId, user]);

  const date = cert ? new Date(cert.issued_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "";

  return (
    <div className="bg-brand-alabaster min-h-screen">
      <div className="print:hidden">
        <Navbar />
        <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-10 flex items-center justify-between">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-brand-pink" data-testid="cert-back">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>
          <Button
            onClick={() => window.print()}
            data-testid="cert-print-btn"
            className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white"
          >
            <Download className="w-4 h-4 mr-2" /> Save / Print PDF
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
        {!user && (
          <div className="text-center py-24">
            <div className="text-neutral-500 mb-4">Log in to view your certificate.</div>
            <Button onClick={() => nav("/login")} className="rounded-full bg-brand-pink text-white">Log in</Button>
          </div>
        )}
        {user && err && !cert && (
          <div className="text-center py-24">
            <div className="text-neutral-500 mb-4">{err}</div>
            <Button asChild className="rounded-full bg-brand-pink text-white"><Link to={`/courses/${courseId}`}>Back to course</Link></Button>
          </div>
        )}
        {cert && (
          <div className="rounded-[2.5rem] bg-white border-[3px] border-brand-pink/60 shadow-2xl p-12 md:p-20 relative overflow-hidden print:shadow-none print:border-brand-pink" data-testid="certificate-body">
            {/* Corner ornaments */}
            <div className="absolute top-8 left-8 flex items-center gap-2 text-brand-pink">
              <Heart className="w-4 h-4" fill="#E91E63" />
              <span className="text-xs tracking-[0.3em] uppercase">Host Hustle</span>
            </div>
            <div className="absolute top-8 right-8 text-brand-pink">
              <Award className="w-6 h-6" />
            </div>

            <div className="text-center mt-8">
              <div className="uppercase tracking-[0.4em] text-xs text-neutral-500 mb-4">Certificate of Completion</div>
              <div className="font-script text-4xl md:text-5xl text-brand-pink mb-2">Congratulations</div>
              <div className="text-sm text-neutral-500 mb-8">This certificate is proudly presented to</div>

              <div className="font-display text-4xl md:text-6xl leading-tight text-neutral-900 mb-2">{cert.user_name}</div>
              <div className="w-40 h-px bg-neutral-300 mx-auto my-6" />

              <div className="text-neutral-600 max-w-2xl mx-auto text-lg leading-relaxed">
                for successfully completing the course
              </div>
              <div className="font-display text-2xl md:text-3xl italic text-brand-pink mt-3 mb-10">"{cert.course_title}"</div>

              <div className="grid md:grid-cols-2 gap-10 max-w-2xl mx-auto mt-12">
                <div>
                  <div className="font-script text-3xl text-neutral-800 leading-none">Kristy</div>
                  <div className="w-40 h-px bg-neutral-300 mx-auto my-2" />
                  <div className="text-xs uppercase tracking-widest text-neutral-500">Founder, Host Hustle™</div>
                </div>
                <div>
                  <div className="font-display text-xl text-neutral-800">{date}</div>
                  <div className="w-40 h-px bg-neutral-300 mx-auto my-2" />
                  <div className="text-xs uppercase tracking-widest text-neutral-500">Date Issued</div>
                </div>
              </div>

              <div className="mt-12 text-[10px] uppercase tracking-[0.3em] text-neutral-400">Certificate ID · {cert.id}</div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}
